import { QueryClient } from '@tanstack/react-query';

/**
 * React Query client configuration
 * Centralized query client with optimized defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes - data is fresh for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Cache time: 30 minutes - cached data kept for 30 minutes
      gcTime: 1000 * 60 * 30,
      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (useful for real-time data)
      refetchOnWindowFocus: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
      // Don't refetch on reconnect by default
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Query keys factory for consistent key management
 * Use this to ensure type-safe and consistent query keys
 */
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Packages (Sender)
  packages: {
    all: ['packages'] as const,
    lists: () => [...queryKeys.packages.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.packages.lists(), filters] as const,
    details: () => [...queryKeys.packages.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.packages.details(), id] as const,
  },

  // Itineraries (Jetpacker)
  itineraries: {
    all: ['itineraries'] as const,
    lists: () => [...queryKeys.itineraries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.itineraries.lists(), filters] as const,
    details: () => [...queryKeys.itineraries.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.itineraries.details(), id] as const,
  },

  // Deliveries
  deliveries: {
    all: ['deliveries'] as const,
    lists: () => [...queryKeys.deliveries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.deliveries.lists(), filters] as const,
    details: () => [...queryKeys.deliveries.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.deliveries.details(), id] as const,
  },

  // Chat
  chat: {
    all: ['chat'] as const,
    conversations: () => [...queryKeys.chat.all, 'conversations'] as const,
    messages: (conversationId: string) =>
      [...queryKeys.chat.all, 'messages', conversationId] as const,
  },

  // User profile
  profile: {
    all: ['profile'] as const,
    me: () => [...queryKeys.profile.all, 'me'] as const,
    user: (id: string) => [...queryKeys.profile.all, id] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const,
  },

  // JettBoard (Sender dashboard)
  jettboard: {
    all: ['jettboard'] as const,
    summary: () => [...queryKeys.jettboard.all, 'summary'] as const,
  },

  // Earnings (Jetpacker)
  earnings: {
    all: ['earnings'] as const,
    summary: () => [...queryKeys.earnings.all, 'summary'] as const,
    history: (filters: Record<string, unknown>) =>
      [...queryKeys.earnings.all, 'history', filters] as const,
  },
} as const;

/**
 * Helper function to invalidate related queries
 */
export const invalidateQueries = {
  packages: () => queryClient.invalidateQueries({ queryKey: queryKeys.packages.all }),
  itineraries: () => queryClient.invalidateQueries({ queryKey: queryKeys.itineraries.all }),
  deliveries: () => queryClient.invalidateQueries({ queryKey: queryKeys.deliveries.all }),
  chat: () => queryClient.invalidateQueries({ queryKey: queryKeys.chat.all }),
  profile: () => queryClient.invalidateQueries({ queryKey: queryKeys.profile.all }),
  notifications: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
  jettboard: () => queryClient.invalidateQueries({ queryKey: queryKeys.jettboard.all }),
  earnings: () => queryClient.invalidateQueries({ queryKey: queryKeys.earnings.all }),
  all: () => queryClient.invalidateQueries(),
};
